import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  BarChart3, 
  FileSpreadsheet, 
  Smartphone, 
  Shield, 
  Zap,
  ArrowRight,
  CheckCircle2,
  Menu,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';

const Landing = () => {
  const [menuOpen, setMenuOpen] = React.useState(false);

  const features = [
    {
      icon: Building2,
      title: 'Control de Obras',
      description: 'Gestiona múltiples proyectos desde un solo lugar con visibilidad total del avance y presupuesto.'
    },
    {
      icon: FileSpreadsheet,
      title: 'Catálogos Inteligentes',
      description: 'Organiza partidas con filtros tipo Excel, asigna contratistas y controla volúmenes ejecutados.'
    },
    {
      icon: BarChart3,
      title: 'Reportes en Tiempo Real',
      description: 'Exporta a Excel y PDF, visualiza dashboards y toma decisiones basadas en datos.'
    },
    {
      icon: Smartphone,
      title: '100% Web Responsive',
      description: 'Accede desde cualquier dispositivo. Diseñado para usarse en obra con una sola mano.'
    },
    {
      icon: Shield,
      title: 'Permisos por Rol',
      description: 'Desarrolladores y constructores con accesos diferenciados según su responsabilidad.'
    },
    {
      icon: Zap,
      title: 'Rápido y Simple',
      description: 'Sin apps que instalar. Interfaz intuitiva que tu equipo dominará en minutos.'
    }
  ];

  const benefits = [
    'Visibilidad completa del presupuesto vs ejecutado',
    'Captura de avances con evidencia fotográfica',
    'Flujo de cotizaciones y aprobaciones',
    'Documentos y planos centralizados',
    'Calendario de obra con fechas clave',
    'Exportación a Excel y PDF'
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] relative noise-overlay">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#C05621] rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl text-[#2D2A26]">BROCO</span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-[#5C554B] hover:text-[#C05621] font-medium transition-colors">
                Características
              </a>
              <a href="#benefits" className="text-[#5C554B] hover:text-[#C05621] font-medium transition-colors">
                Beneficios
              </a>
              <a href="#how-it-works" className="text-[#5C554B] hover:text-[#C05621] font-medium transition-colors">
                Cómo funciona
              </a>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-[#5C554B] hover:text-[#C05621]" data-testid="login-btn">
                  Iniciar sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary" data-testid="register-btn">
                  Registrarse
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 rounded-lg hover:bg-[#EBE5DA] transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-[#E2E8F0] py-4 px-4 animate-fade-in">
            <div className="flex flex-col gap-4">
              <a href="#features" className="text-[#5C554B] font-medium py-2" onClick={() => setMenuOpen(false)}>
                Características
              </a>
              <a href="#benefits" className="text-[#5C554B] font-medium py-2" onClick={() => setMenuOpen(false)}>
                Beneficios
              </a>
              <a href="#how-it-works" className="text-[#5C554B] font-medium py-2" onClick={() => setMenuOpen(false)}>
                Cómo funciona
              </a>
              <div className="flex flex-col gap-2 pt-4 border-t border-[#E2E8F0]">
                <Link to="/login">
                  <Button variant="outline" className="w-full">Iniciar sesión</Button>
                </Link>
                <Link to="/register">
                  <Button className="btn-primary w-full">Registrarse</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-slide-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C05621]/10 text-[#C05621] font-medium text-sm">
                <Zap className="w-4 h-4" />
                ERP de Obra 100% Web
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2D2A26] tracking-tight leading-tight">
                Controla tus obras desde{' '}
                <span className="text-[#C05621]">cualquier lugar</span>
              </h1>
              
              <p className="text-lg md:text-xl text-[#5C554B] leading-relaxed max-w-xl">
                BROCO es el sistema de gestión de obra que tu equipo necesita. 
                Sin apps, sin complicaciones. Solo resultados.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button className="btn-primary text-lg h-14 px-8 w-full sm:w-auto" data-testid="hero-cta">
                    Comenzar gratis
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="#how-it-works">
                  <Button variant="outline" className="btn-secondary text-lg h-14 px-8 w-full sm:w-auto">
                    Ver cómo funciona
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative animate-slide-up stagger-1">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1623851277328-fca6cd2b8eec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwc3Vuc2V0fGVufDB8fHx8MTc3MDI0MzgxMHww&ixlib=rb-4.1.0&q=85"
                  alt="Construcción moderna"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2D2A26]/60 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="glass rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#C05621] rounded-full flex items-center justify-center">
                        <BarChart3 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#2D2A26]">Avance del proyecto</p>
                        <p className="text-[#5C554B]">78% completado</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2D2A26] mb-4">
              Todo lo que necesitas para gestionar tu obra
            </h2>
            <p className="text-lg text-[#5C554B] max-w-2xl mx-auto">
              Herramientas diseñadas para el día a día de la construcción
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="card-dashboard p-8 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 bg-[#C05621]/10 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-[#C05621]" />
                </div>
                <h3 className="text-xl font-semibold text-[#2D2A26] mb-3">{feature.title}</h3>
                <p className="text-[#5C554B] leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-16 md:py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1714575600356-6635434699f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxhcmNoaXRlY3QlMjB3aXRoJTIwdGFibGV0JTIwb24lMjBjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3MDI0MzgxM3ww&ixlib=rb-4.1.0&q=85"
                alt="Arquitecto con tablet"
                className="rounded-2xl shadow-xl"
              />
            </div>

            <div className="space-y-8">
              <h2 className="text-3xl md:text-4xl font-bold text-[#2D2A26]">
                Beneficios que transforman tu operación
              </h2>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div 
                    key={benefit}
                    className="flex items-start gap-3 animate-fade-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <CheckCircle2 className="w-6 h-6 text-[#81A183] flex-shrink-0 mt-0.5" />
                    <span className="text-[#5C554B] text-lg">{benefit}</span>
                  </div>
                ))}
              </div>

              <Link to="/register">
                <Button className="btn-primary mt-4">
                  Probar BROCO gratis
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2D2A26] mb-4">
              Cómo funciona
            </h2>
            <p className="text-lg text-[#5C554B] max-w-2xl mx-auto">
              Empieza a usar BROCO en tres simples pasos
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Regístrate', desc: 'Crea tu cuenta como desarrollador o constructor en segundos.' },
              { step: '02', title: 'Crea tu obra', desc: 'Agrega proyectos, catálogos y asigna a tu equipo.' },
              { step: '03', title: 'Gestiona', desc: 'Cotiza, captura avances y genera reportes desde cualquier lugar.' }
            ].map((item, index) => (
              <div key={item.step} className="text-center animate-fade-in" style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="w-20 h-20 bg-[#C05621] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-[#2D2A26] mb-3">{item.title}</h3>
                <p className="text-[#5C554B]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-[#C05621] rounded-3xl p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Comienza a controlar tus obras hoy
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-xl mx-auto">
              Únete a desarrolladores y constructores que ya optimizan su gestión con BROCO
            </p>
            <Link to="/register">
              <Button className="bg-white text-[#C05621] hover:bg-[#FDFBF7] rounded-full px-8 py-4 text-lg font-semibold">
                Crear cuenta gratis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#C05621] rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-[#2D2A26]">BROCO</span>
          </div>
          <p className="text-[#5C554B] text-sm">
            © 2024 BROCO. ERP de Obra. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
